import { useCallback, useEffect, useRef, useState } from 'react';
import { contractApi, type SigningRoomActivity, type SigningRoomSession } from '../services/api';
import type { Contract } from '../types';
import { signingReturnResult } from '../utils/signingReturn';
import { validZignedSigningUrl } from '../utils/signingNavigation';

export const MAX_SIGNING_ROOM_ATTEMPTS = 3;
type Phase = 'preparing' | 'loading' | 'ready' | 'active' | 'signing' | 'confirming' | 'error';
type RoomError = 'open' | 'load' | 'status' | 'verification' | null;

export function useSigningRoom({ contractId, processId, enabled, persona, onComplete }: {
  contractId: string | undefined;
  processId: string | undefined;
  enabled: boolean;
  persona: string | undefined;
  onComplete: (contract: Contract) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const completion = useRef(onComplete);
  completion.current = onComplete;
  const [generation, setGeneration] = useState(0);
  const [session, setSession] = useState<SigningRoomSession | null>(null);
  const [attempt, setAttempt] = useState(1);
  const [phase, setPhase] = useState<Phase>('preparing');
  const [error, setError] = useState<RoomError>(null);

  const retry = useCallback(() => setGeneration(value => value + 1), []);

  useEffect(() => {
    setSession(null);
    setAttempt(1);
    setPhase('preparing');
    setError(null);
    if (!enabled || !contractId || !processId) return;
    let disposed = false;
    void contractApi.prepareSigningRoom(contractId, processId).then(value => {
      if (disposed) return;
      if (value.processId !== processId || value.contractId !== contractId || !validZignedSigningUrl(value.signingUrl)) {
        throw new Error('Invalid signing room.');
      }
      setSession(value);
      setPhase('loading');
    }).catch(() => {
      if (!disposed) { setPhase('error'); setError('open'); }
    });
    return () => { disposed = true; };
  }, [contractId, processId, enabled, generation]);

  useEffect(() => {
    if (!enabled || !session || !contractId) return;
    let disposed = false;
    let finished = false;
    let attempts = 1;
    let attemptStartedAt = Date.now();
    let loaded = false;
    let activityObserved = false;
    let signingStarted = false;
    let returning = false;
    let lastFailureId: string | null = null;
    let lastSyncAt = -Infinity;
    let syncing = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const timeoutMs = Math.max(10_000, session.loadPolicy.timeoutMs);

    const completeIfConfirmed = (contract: Contract) => {
      if (disposed || finished || contract.externalSigningProcessId !== session.processId) return;
      if (signingReturnResult(contract, persona) === 'pending') return;
      finished = true;
      clearTimeout(timer);
      completion.current(contract);
    };
    const sync = async () => {
      if (syncing || disposed || finished) return;
      syncing = true;
      lastSyncAt = Date.now();
      try {
        const value = await contractApi.syncSigning(contractId, 10_000);
        completeIfConfirmed(value.contract);
      } catch {
        // A failed status check never implies that the document failed to load.
      } finally { syncing = false; }
    };
    const nextAttempt = () => {
      if (loaded || activityObserved || signingStarted || returning || disposed || finished || document.hidden) return;
      if (attempts >= MAX_SIGNING_ROOM_ATTEMPTS) {
        setPhase('error'); setError('load');
        return;
      }
      attempts += 1;
      attemptStartedAt = Date.now();
      setAttempt(attempts);
      setPhase('loading');
      setError(null);
    };
    const applyActivity = (value: SigningRoomActivity) => {
      if (disposed || finished || value.processId !== session.processId) return;
      completeIfConfirmed(value.contract);
      if (finished) return;
      if (value.activityObserved) {
        activityObserved = true;
        if (!loaded && !signingStarted && !returning) setPhase('active');
        setError(null);
      }
      if (value.signingStarted) {
        signingStarted = true;
        setPhase(returning ? 'confirming' : 'signing'); setError(null);
      }
      if (value.document.state === 'loaded') {
        loaded = true;
        if (!signingStarted && !returning) setPhase('ready');
        setError(null);
      }
    };
    const verifyBeforeReload = async () => {
      // The live provider check is only needed before a possible reload, not
      // on every webhook poll. Unknown state never authorizes a reload.
      attemptStartedAt = Date.now();
      try {
        const verified = await contractApi.signingRoomActivity(contractId, session.processId, session.startedAt, controller.signal, true);
        if (disposed || finished || verified.processId !== session.processId) return;
        applyActivity(verified);
        if (loaded || activityObserved || signingStarted || returning || finished || document.hidden) return;
        if (verified.reloadAllowed === true && verified.document.state === 'failed') nextAttempt();
        else setError('verification');
      } catch {
        if (!disposed && !finished && !loaded && !activityObserved && !signingStarted && !returning) setError('status');
      }
    };
    const poll = async () => {
      if (disposed || finished) return;
      if (!document.hidden) {
        if (Date.now() - lastSyncAt >= 5000) void sync();
        try {
          const value = await contractApi.signingRoomActivity(contractId, session.processId, session.startedAt, controller.signal);
          if (disposed || finished || value.processId !== session.processId) return;
          setError(previous => previous === 'status' ? null : previous);
          applyActivity(value);
          if (finished) return;
          if (!loaded && !activityObserved && !signingStarted && !returning) {
            const failed = value.document.state === 'failed' && value.document.eventId !== lastFailureId;
            if (failed) lastFailureId = value.document.eventId;
            if (failed || Date.now() - attemptStartedAt >= timeoutMs) await verifyBeforeReload();
          }
        } catch {
          if (!disposed && !finished) setError('status');
        }
      }
      if (!disposed && !finished) timer = setTimeout(poll, 1000);
    };
    const onReturn = (event: MessageEvent) => {
      if (event.origin !== session.callbackOrigin || event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || data.type !== 'etappio:signing-return' || data.contractId !== contractId
        || data.projectId !== session.projectId || data.processId !== session.processId) return;
      returning = true;
      setPhase('confirming'); setError(null);
      void sync(); // A return message is a hint, never proof of a signature.
    };
    const onVisibility = () => {
      // A mobile BankID visit or a background tab must not consume the load budget.
      if (!document.hidden) { attemptStartedAt = Date.now(); void sync(); }
    };
    window.addEventListener('message', onReturn);
    document.addEventListener('visibilitychange', onVisibility);
    void poll();
    return () => {
      disposed = true;
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener('message', onReturn);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, session, contractId, persona]);

  return { iframeRef, session, attempt, phase, error, retry, frameKey: `${generation}:${attempt}` };
}
